import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import {
  getWebinarListApi,
  WebinarListItem,
  WebinarListRole,
  WebinarListStatus,
} from '@geneo2-web/services-clients';
import { IStyles } from '../../../commonUtils/styleUtils';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { theme } from '../../../theme/themeProvider';
import { NoContentCard } from '../NoContent.tsx';
import { IconWrapper } from '../../elements/IconWrapper/Index';

const PAGE_LIMIT = 10;

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(12), md: pxTovW(14) },
    mt: { xs: pxToRem(16), md: pxTovW(20) },
    width: '100%',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(8), md: pxTovW(12) },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
  },
  arrowGroup: {
    display: { xs: 'none', md: 'flex' },
    alignItems: 'center',
    gap: pxTovW(8),
  },
  carouselButton: {
    margin: '0px',
    minWidth: 0,
    padding: '0px !important',
    width: { md: pxTovW(36) },
    height: { md: pxTovW(36) },
    borderRadius: { md: pxTovW(8) },
    border: '1px solid grey',
    background: 'white',
    color: 'text.primary',
    flexShrink: 0,
    '&:hover': {
      background: 'white',
    },
    '&.Mui-disabled': {
      opacity: 0.4,
    },
  },
  tabs: {
    minHeight: 'unset',
    '& .MuiTab-root': {
      textTransform: 'none',
      minHeight: 'unset',
      py: { xs: pxToRem(8), md: pxTovW(10) },
    },
  },
  carouselRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0, md: pxTovW(10) },
    width: '100%',
  },
  cardsViewport: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'repeat(2, minmax(0, 1fr))',
    },
    gap: { xs: pxToRem(12), md: pxTovW(16) },
    overflowX: { xs: 'auto', md: 'hidden' },
    '&::-webkit-scrollbar': { display: 'none' },
  },
  card: {
    height: { xs: pxToRem(148), md: pxTovW(199) },
    width: '100%',
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DFDE',
    padding: {
      xs: pxToRem(8),
      md: `${pxTovW(13)} ${pxTovW(12)}`,
    },
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(12), md: pxTovW(16) },
    boxSizing: 'border-box',
    boxShadow: `0px 0px ${pxToRem(11)} #E7E7E7D9`,
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': {
      boxShadow: { md: `0px 0px ${pxToRem(14)} #D0D0D0D9` },
    },
  },
  imageBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: { xs: '100%', md: pxTovW(157) },
    width: { xs: pxToRem(90), md: pxTovW(140) },
    minWidth: { xs: pxToRem(90), md: pxTovW(140) },
    boxSizing: 'border-box',
    borderRadius: { xs: pxToRem(12), md: pxTovW(16) },
    backgroundColor: '#E6F3FF',
    position: 'relative',
    overflow: 'hidden',
  },
  statusBadge: {
    position: 'absolute',
    top: 0,
    left: 0,
    px: { xs: pxToRem(6), md: pxTovW(8) },
    py: { xs: pxToRem(2), md: pxTovW(3) },
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'space-between',
    minWidth: 0,
    py: { xs: pxToRem(2), md: pxTovW(4) },
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    fontWeight: 700,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    minWidth: 0,
  },
  metaText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'neutral.duskySteel',
  },
  blueText: {
    color: 'secondary.main',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
  },
  idPwd: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(2), md: pxTovW(4) },
    minWidth: 0,
    flex: 1,
  },
  idPwdText: {
    color: 'neutral.duskySteel',
    display: 'block',
    lineHeight: 1.4,
    wordBreak: 'break-all',
  },
  metaLabel: {
    color: 'neutral.duskySteel',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(4),
    mb: { xs: pxToRem(10), md: pxTovW(12) },
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    pr: 1,
  },
};

function formatDateTime(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatShortTime(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getMeetingActionUrl(
  item: WebinarListItem,
  linkType: 'start' | 'join'
) {
  if (linkType === 'start') {
    return item.slot?.zoomStartUrl || item.slot?.zoomJoinUrl;
  }
  return item.slot?.zoomJoinUrl || item.slot?.zoomStartUrl;
}

function isZoomMeetingId(id?: string) {
  return !!id && /^\d+$/.test(id);
}

function WebinarCard(props: {
  item: WebinarListItem;
  linkType: 'start' | 'join';
  status: WebinarListStatus;
  onClick: () => void;
}) {
  const { item, linkType, status, onClick } = props;
  const url = getMeetingActionUrl(item, linkType);
  const meetingId = isZoomMeetingId(item.slot?.zoomMeetingId)
    ? item.slot?.zoomMeetingId
    : undefined;
  const meetingPassword = item.slot?.zoomPassword || undefined;
  const gradeSection = [item.webinar?.grade, item.webinar?.section]
    .filter(Boolean)
    .join(' ');

  return (
    <Box sx={styles.card} onClick={onClick}>
      <Box sx={styles.imageBox}>
        <Box sx={styles.statusBadge}>
          <Typography
            variant="smallText"
            color={status === WebinarListStatus.ONGOING ? '#007CDC' : '#666'}
          >
            {status === WebinarListStatus.ONGOING ? 'Ongoing' : 'Ended'}
          </Typography>
        </Box>
        <VideocamOutlinedIcon
          sx={{
            fontSize: { xs: pxToRem(40), md: pxTovW(56) },
            color: '#007CDC',
          }}
        />
      </Box>

      <Box sx={styles.content}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(4), md: pxTovW(6) },
            minWidth: 0,
          }}
        >
          {gradeSection ? (
            <Typography variant="subText" sx={styles.blueText}>
              {gradeSection}
              {item.webinar?.hostName ? ` · ${item.webinar.hostName}` : ''}
            </Typography>
          ) : (
            <Typography variant="subText" sx={styles.blueText}>
              {item.webinar?.hostName || 'Webinar'}
            </Typography>
          )}
          <Typography variant="h3" sx={styles.title}>
            {item.webinar?.title || 'Untitled webinar'}
          </Typography>
          <Box sx={styles.metaRow}>
            <IconWrapper
              name="clock"
              type="png"
              parentFolder="icons"
              size="small"
            />
            <Typography variant="subText" sx={styles.metaText}>
              {formatShortTime(item.slot?.startTime)} –{' '}
              {formatShortTime(item.slot?.endTime)}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.cardFooter}>
          <Box sx={styles.idPwd}>
            {meetingId ? (
              <Typography variant="subText" sx={styles.idPwdText}>
                ID: {meetingId}
              </Typography>
            ) : null}
            {meetingPassword ? (
              <Typography variant="subText" sx={styles.idPwdText}>
                Pwd: {meetingPassword}
              </Typography>
            ) : null}
          </Box>
          {url && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                minWidth: 'unset',
                px: { xs: pxToRem(10), md: pxTovW(12) },
              }}
            >
              {linkType === 'start' ? 'Start' : 'Join'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export interface WebinarListSectionProps {
  role: WebinarListRole;
  profileId: number;
  schoolId: number;
  title?: string;
  /** Prefer Zoom start URL (admin/teacher) or join URL (student). */
  linkType?: 'start' | 'join';
  /** Change to force a refetch (e.g. after create). */
  refreshKey?: number;
  /**
   * When true, render nothing if there are no webinars in Ongoing or Ended
   * (avoids an empty section on home screens).
   */
  hideWhenEmpty?: boolean;
}

export function WebinarListSection(props: WebinarListSectionProps) {
  const {
    role,
    profileId,
    schoolId,
    title = 'Webinars',
    linkType = 'join',
    refreshKey = 0,
    hideWhenEmpty = false,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const itemsPerPage = isMobile ? 1 : 2;

  const [tab, setTab] = useState<WebinarListStatus>(WebinarListStatus.ONGOING);
  const [apiPage, setApiPage] = useState(1);
  const [items, setItems] = useState<WebinarListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<WebinarListItem | null>(null);
  const [checkingEmpty, setCheckingEmpty] = useState(hideWhenEmpty);
  const [hasAnyWebinars, setHasAnyWebinars] = useState(!hideWhenEmpty);

  const hasMoreFromApi = items.length < totalCount;
  const canGoPrev = currentIndex > 0;
  const canGoNextLocal = currentIndex + itemsPerPage < items.length;
  const canGoNext = canGoNextLocal || hasMoreFromApi;

  const visibleItems = items.slice(currentIndex, currentIndex + itemsPerPage);

  const fetchApiPage = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (!profileId || !schoolId) return false;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(false);
      try {
        const response = await getWebinarListApi({
          role,
          profileId,
          schoolId,
          status: tab,
          pagination: { page: pageToLoad, limit: PAGE_LIMIT },
        });
        const nextItems = response.items ?? [];
        setTotalCount(response.totalCount ?? 0);
        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setApiPage(pageToLoad);
        return nextItems.length > 0;
      } catch (err) {
        console.error(err);
        setError(true);
        if (!append) {
          setItems([]);
          setTotalCount(0);
        }
        return false;
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [role, profileId, schoolId, tab]
  );

  // When hideWhenEmpty: probe both statuses before mounting the section UI.
  useEffect(() => {
    if (!hideWhenEmpty || !profileId || !schoolId) {
      setHasAnyWebinars(true);
      setCheckingEmpty(false);
      return;
    }

    let cancelled = false;
    setCheckingEmpty(true);

    (async () => {
      try {
        const pagination = { page: 1, limit: 1 };
        const [ongoing, ended] = await Promise.all([
          getWebinarListApi({
            role,
            profileId,
            schoolId,
            status: WebinarListStatus.ONGOING,
            pagination,
          }),
          getWebinarListApi({
            role,
            profileId,
            schoolId,
            status: WebinarListStatus.ENDED,
            pagination,
          }),
        ]);
        if (cancelled) return;
        const any =
          (ongoing.totalCount ?? 0) > 0 ||
          (ongoing.items?.length ?? 0) > 0 ||
          (ended.totalCount ?? 0) > 0 ||
          (ended.items?.length ?? 0) > 0;
        setHasAnyWebinars(any);
      } catch (err) {
        console.error(err);
        if (!cancelled) setHasAnyWebinars(false);
      } finally {
        if (!cancelled) setCheckingEmpty(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hideWhenEmpty, role, profileId, schoolId, refreshKey]);

  // Initial / filter change: fetch page 1 only (skip while empty-check hides the UI)
  useEffect(() => {
    if (hideWhenEmpty && (checkingEmpty || !hasAnyWebinars)) return;
    setItems([]);
    setTotalCount(0);
    setApiPage(1);
    setCurrentIndex(0);
    fetchApiPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    role,
    profileId,
    schoolId,
    tab,
    refreshKey,
    hideWhenEmpty,
    checkingEmpty,
    hasAnyWebinars,
  ]);

  // Keep index valid when itemsPerPage changes (mobile/desktop toggle)
  useEffect(() => {
    setCurrentIndex((idx) =>
      Math.floor(idx / itemsPerPage) * itemsPerPage
    );
  }, [itemsPerPage]);

  if (hideWhenEmpty && (checkingEmpty || !hasAnyWebinars)) {
    return null;
  }

  const handleClickPrev = () => {
    if (!canGoPrev || loadingMore) return;
    setCurrentIndex((idx) => Math.max(0, idx - itemsPerPage));
  };

  const handleClickNext = async () => {
    if (!canGoNext || loadingMore || loading) return;

    // Still have buffered cards — no API call
    if (canGoNextLocal) {
      setCurrentIndex((idx) => idx + itemsPerPage);
      return;
    }

    // Need next API page before advancing
    if (!hasMoreFromApi) return;
    const loaded = await fetchApiPage(apiPage + 1, true);
    if (loaded) {
      setCurrentIndex((idx) => idx + itemsPerPage);
    }
  };

  const meetingUrl = selected
    ? getMeetingActionUrl(selected, linkType)
    : undefined;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headerRow}>
        <Box sx={styles.titleRow}>
          <Typography variant="h2" fontWeight={700}>
            {title}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value: WebinarListStatus) => {
          setTab(value);
        }}
        sx={styles.tabs}
      >
        <Tab label="Ongoing" value={WebinarListStatus.ONGOING} />
        <Tab label="Ended" value={WebinarListStatus.ENDED} />
      </Tabs>

      {loading && (
        <Typography variant="h4" sx={styles.metaLabel}>
          Loading webinars...
        </Typography>
      )}

      {!loading && error && items.length === 0 && (
        <NoContentCard
          variant="error"
          icon="error"
          text="Failed to load webinars"
        />
      )}

      {!loading && !error && items.length === 0 && (
        <NoContentCard
          variant="info"
          icon="cards"
          text={
            tab === WebinarListStatus.ONGOING
              ? 'No ongoing webinars'
              : 'No ended webinars'
          }
        />
      )}

      {items.length > 0 && (
        <Box sx={styles.carouselRow}>
          <Button
            sx={{
              ...styles.carouselButton,
              display: { xs: 'none', md: 'flex' },
            }}
            onClick={handleClickPrev}
            disabled={!canGoPrev || loadingMore}
            aria-label="Previous webinars"
          >
            <KeyboardArrowLeftIcon />
          </Button>

          <Box sx={styles.cardsViewport}>
            {visibleItems.map((item, index) => {
              const key = `${item.slot?.id ?? 's'}-${item.webinar?.id ?? 'w'}-${
                currentIndex + index
              }`;
              return (
                <WebinarCard
                  key={key}
                  item={item}
                  linkType={linkType}
                  status={tab}
                  onClick={() => setSelected(item)}
                />
              );
            })}
          </Box>

          <Button
            sx={{
              ...styles.carouselButton,
              display: { xs: 'none', md: 'flex' },
            }}
            onClick={handleClickNext}
            disabled={!canGoNext || loadingMore || loading}
            aria-label="Next webinars"
          >
            <KeyboardArrowRightIcon />
          </Button>
        </Box>
      )}

      {loadingMore && (
        <Typography variant="h4" sx={styles.metaLabel}>
          Loading more...
        </Typography>
      )}

      {/* Mobile: simple prev/next under cards */}
      {items.length > 0 && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            gap: pxToRem(12),
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClickPrev}
            disabled={!canGoPrev || loadingMore}
            startIcon={<KeyboardArrowLeftIcon />}
          >
            Prev
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClickNext}
            disabled={!canGoNext || loadingMore || loading}
            endIcon={<KeyboardArrowRightIcon />}
          >
            Next
          </Button>
        </Box>
      )}

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={styles.dialogTitleRow}>
          <Typography variant="h2" fontWeight={700}>
            {selected?.webinar?.title || 'Webinar details'}
          </Typography>
          <IconButton onClick={() => setSelected(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Description
                </Typography>
                <Typography variant="h4">
                  {selected.webinar?.description || '—'}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Host
                </Typography>
                <Typography variant="h4">
                  {selected.webinar?.hostName || '—'}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Host Email
                </Typography>
                <Typography variant="h4">
                  {selected.webinar?.hostZoomEmail || '—'}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Grade / Section
                </Typography>
                <Typography variant="h4">
                  {[selected.webinar?.grade, selected.webinar?.section]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Invite emails
                </Typography>
                <Typography variant="h4">
                  {selected.webinar?.inviteEmails?.length
                    ? selected.webinar.inviteEmails.join(', ')
                    : '—'}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Schedule
                </Typography>
                <Typography variant="h4">
                  {formatDateTime(selected.slot?.startTime)} –{' '}
                  {formatDateTime(selected.slot?.endTime)}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography variant="cardText" sx={styles.metaLabel}>
                  Meeting ID
                </Typography>
                <Typography variant="h4">
                  {selected.slot?.zoomMeetingId || '—'}
                </Typography>
              </Box>
              {selected.slot?.zoomPassword ? (
                <Box sx={styles.detailRow}>
                  <Typography variant="cardText" sx={styles.metaLabel}>
                    Password
                  </Typography>
                  <Typography variant="h4">
                    {selected.slot.zoomPassword}
                  </Typography>
                </Box>
              ) : null}
              {meetingUrl && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    window.open(meetingUrl, '_blank', 'noopener,noreferrer')
                  }
                >
                  {linkType === 'start' ? 'Start Zoom' : 'Join Zoom'}
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default WebinarListSection;
